/* eslint-disable react/function-component-definition */
import React, { Fragment, useMemo } from 'react';
import { Box, Grid, Button, Tooltip } from '@mui/material/';
import { ITask } from 'interfaces/redux/ITasks';
import { useTypedSelector } from 'hooks/use-typed-selector';
import AddIcon from '@mui/icons-material/Add';
import { windowFeatureIsEnabled } from 'config/window-features';
import { THERAPY_MED_SYNC } from '../../../../constants';
import { NeedsByDateDialogForm } from '../forms/needsby-date-task-therapy';
import { FollowUpDateDialogForm } from '../forms/followup-date-task-form';

import { Styles } from './table.styles';

interface TaskTableActionSectionProps {
  tasks: ITask[];
  patientId: number;
  handleDisplayAddTaskForm: () => void;
  addTaskOpen: boolean;
}

export const TaskTableActionSection: React.FC<TaskTableActionSectionProps> = (
  props,
): JSX.Element => {
  const currentUserActive = useTypedSelector(state => state?.auth?.currentUser?.active);
  const patient = useTypedSelector(state => state.patient);
  const therapiesSource = useTypedSelector(state => state.therapies.data);
  const fcOrdersEnabled = windowFeatureIsEnabled('fc_orders_task_table');

  const fcTasks = useMemo(() => {
    return props.tasks
      .filter(task => task.taskType === 'FC')
      .map(task => {
        if (therapiesSource[task.therapy_id]) {
          return {
            ...task,
            in_med_sync: therapiesSource[task.therapy_id].in_med_sync,
          };
        }
        return {
          ...task,
          in_med_sync: 0,
        };
      }) as (ITask & { in_med_sync: number })[];
  }, [therapiesSource, props.tasks]);

  const eligibleFcTasks = useMemo(() => {
    return fcTasks.filter(
      task => therapiesSource?.[task.therapy_id]?.fill_cycle === task.fill_cycle_number,
    );
  }, [fcTasks, therapiesSource]);

  const orderEligibleTherapiesForNbd = useMemo(() => {
    if (!fcOrdersEnabled) {
      return [] as Array<{
        id: number;
        patient_id: number;
        in_med_sync: number | null | undefined;
      }>;
    }

    const selectedTherapies = new Map<
      number,
      { id: number; patient_id: number; in_med_sync: number | null | undefined }
    >();

    props.tasks
      .filter(task => task.taskType === 'Order')
      .forEach(task => {
        const taskTherapyIds = (task.therapy_ids || '')
          .split(',')
          .map(id => Number(id.trim()))
          .filter(id => Number.isInteger(id) && id > 0);

        taskTherapyIds.forEach(therapyId => {
          const therapy = therapiesSource?.[therapyId];
          if (!therapy) {
            return;
          }

          selectedTherapies.set(therapyId, {
            id: therapyId,
            patient_id: props.patientId,
            in_med_sync: therapy.in_med_sync,
          });
        });
      });

    return Array.from(selectedTherapies.values());
  }, [fcOrdersEnabled, props.patientId, props.tasks, therapiesSource]);

  const nbdButtonEligibleTherapies = useMemo(() => {
    const fcTherapies = eligibleFcTasks.map(item => {
      return {
        id: item.therapy_id,
        patient_id: props.patientId,
        in_med_sync: item.in_med_sync,
      };
    });

    if (!fcOrdersEnabled) {
      return fcTherapies;
    }

    const mergedTherapies = new Map<
      number,
      { id: number; patient_id: number; in_med_sync: number | null | undefined }
    >();

    fcTherapies.forEach(therapy => {
      mergedTherapies.set(therapy.id, therapy);
    });

    orderEligibleTherapiesForNbd.forEach(therapy => {
      mergedTherapies.set(therapy.id, therapy);
    });

    return Array.from(mergedTherapies.values());
  }, [eligibleFcTasks, fcOrdersEnabled, orderEligibleTherapiesForNbd, props.patientId]);

  const hasNonEligibleTaskTypeSelection = useMemo(() => {
    return props.tasks.some(
      task => task.taskType !== 'FC' && (task.taskType !== 'Order' || !fcOrdersEnabled),
    );
  }, [fcOrdersEnabled, props.tasks]);

  const inMedSync = useMemo(() => {
    return nbdButtonEligibleTherapies.some(t => {
      return ((t && t.in_med_sync) ?? null) === THERAPY_MED_SYNC.IN_MED_SYNC;
    });
  }, [nbdButtonEligibleTherapies]);
  return (
    <Box width="100%" py={2}>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <FollowUpDateDialogForm
            titleOverride="Select follow-up date for selected items"
            tasks={props.tasks}
            disabled={props.tasks.length === 0 || props.addTaskOpen}
          />
        </Grid>
        <Grid item>
          <Tooltip
            title={
              inMedSync ? 'Therapy is part of med sync, change the Anchor Date to move the NBD' : ''
            }
            placement="top"
          >
            <Box>
              <NeedsByDateDialogForm
                therapy={nbdButtonEligibleTherapies}
                disabled={
                  nbdButtonEligibleTherapies.length === 0 ||
                  hasNonEligibleTaskTypeSelection ||
                  props.addTaskOpen ||
                  inMedSync
                }
              />
            </Box>
          </Tooltip>
        </Grid>
        <Grid item>
          {currentUserActive && !!patient.active && patient.status !== 'Inactive' && (
            <Button
              sx={Styles.addTaskButton}
              name="add_task_button"
              id="AddTaskButtonTaskTab"
              variant="contained"
              onClick={props.handleDisplayAddTaskForm}
            >
              <AddIcon />
              New Task
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskTableActionSection;
